'use client';

import { forwardRef, useCallback, useState, type ChangeEvent, type ComponentPropsWithoutRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FieldShellLeadIcon,
  fieldShellInnerInputClass,
  fieldShellWrapperClass,
} from '@/components/ui/field-shell';

/** @deprecated Utiliser {@link fieldShellWrapperClass} depuis `@/components/ui/field-shell`. */
export const institutionListSearchShellClass = fieldShellWrapperClass;

export type InstitutionStyledSearchInputProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'size' | 'type'
> & {
  wrapperClassName?: string;
  /** Icône à gauche (défaut : loupe) */
  searchIcon?: LucideIcon;
  /** Affiche un bouton effacer quand la valeur est non vide */
  showClear?: boolean;
  onClear?: () => void;
  /** Variante compacte (ex. filtre dans un panneau liste déroulante) */
  dense?: boolean;
};

/**
 * Champ de recherche pour barres de liste (dashboard institution / RH / admin) :
 * même langage visuel que {@link InstitutionStyledSelect} (carte + anneau primary).
 */
export const InstitutionStyledSearchInput = forwardRef<HTMLInputElement, InstitutionStyledSearchInputProps>(
  function InstitutionStyledSearchInput(
    {
      className,
      wrapperClassName,
      searchIcon: Icon = Search,
      showClear,
      onClear,
      value,
      defaultValue,
      disabled,
      onChange,
      dense,
      ...rest
    },
    ref,
  ) {
    const [uncontrolled, setUncontrolled] = useState(() => (defaultValue != null ? String(defaultValue) : ''));
    const isControlled = value !== undefined;
    const str = isControlled ? String(value ?? '') : uncontrolled;
    const clearVisible = Boolean(showClear && str.length > 0);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) setUncontrolled(e.target.value);
        onChange?.(e);
      },
      [isControlled, onChange],
    );

    const handleClear = useCallback(() => {
      onClear?.();
      if (onChange) {
        const synthetic = { target: { value: '' } } as ChangeEvent<HTMLInputElement>;
        onChange(synthetic);
      }
      if (!isControlled) setUncontrolled('');
    }, [onChange, onClear, isControlled]);

    return (
      <div
        className={cn(
          fieldShellWrapperClass,
          dense && '!min-h-0 gap-2 px-2 py-1.5 text-xs',
          wrapperClassName,
          disabled && 'opacity-60',
        )}
      >
        <FieldShellLeadIcon Icon={Icon} dense={dense} />
        <input
          ref={ref}
          type="text"
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          className={cn(fieldShellInnerInputClass(dense), className)}
          {...rest}
        />
        {clearVisible ? (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'shrink-0 rounded-md p-1 text-[#5B6472] transition-colors hover:text-[#0C1C38]',
              'dark:text-[#B6CAEC] dark:hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
            )}
            aria-label="Effacer la recherche"
          >
            <X className={cn(dense ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
          </button>
        ) : null}
      </div>
    );
  },
);

InstitutionStyledSearchInput.displayName = 'InstitutionStyledSearchInput';
